import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CanvasPreview } from "@/components/CanvasPreview";
import { Loader2, Search, LayoutTemplate, Download, Check } from "lucide-react";
import { toast } from "sonner";

function TplThumb({ t }) {
  if (t.thumbnail) return <img src={t.thumbnail} alt={t.name} className="w-full h-full object-cover" />;
  if (t.canvas && (t.canvas.front || []).length) return <CanvasPreview canvas={t.canvas} />;
  if (t.content)
    return (
      <div className="absolute inset-0 overflow-hidden bg-white">
        <div className="origin-top-left" style={{ width: "420px", transform: "scale(0.42)" }}>
          <div className="p-4 text-[11px] leading-snug text-slate-800 [&_table]:border-collapse [&_td]:p-1 [&_img]:max-h-10 [&_h2]:text-base [&_h3]:text-sm" dangerouslySetInnerHTML={{ __html: t.content }} />
        </div>
      </div>
    );
  return (
    <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
      <LayoutTemplate className="text-white/80" size={40} />
    </div>
  );
}

export function UseTemplateDialog({ open, onOpenChange, categories, getPayload, heading = "Use a Ready Template", bindMode = false, onPick }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItems(null); setPicked(null); setQ("");
    const cat = (categories && categories.length) ? `?category=${encodeURIComponent(categories.join(","))}` : "";
    api.get(`/school/templates${cat}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [open, categories]);

  const filtered = (items || []).filter((t) => !q.trim() || t.name.toLowerCase().includes(q.trim().toLowerCase()));

  const generate = async () => {
    if (!picked) { toast.error("Pick a template first"); return; }
    if (bindMode) { onPick && onPick(picked); onOpenChange(false); return; }
    const payload = getPayload();
    if (!payload) return;
    if ((!payload.student_ids || !payload.student_ids.length) && !payload.class_id) {
      toast.error("Select at least one student"); return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/school/templates/${picked.id}/pdf`,
        { ...payload, origin: window.location.origin }, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
      toast.success(`Generated "${picked.name}"`);
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data;
      toast.error(typeof d === "string" ? d : "Could not generate from template");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="use-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LayoutTemplate size={18} />{heading}</DialogTitle>
          <DialogDescription>Pick a template from the platform catalog and generate it for your selected students.</DialogDescription>
        </DialogHeader>
        <div className="relative max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates..." className="pl-9" data-testid="use-template-search" />
        </div>
        {items === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-400" data-testid="use-template-empty">
            <LayoutTemplate className="mx-auto mb-2 text-slate-300" size={36} />
            No ready templates available{q ? " for your search" : " in this category yet"}.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="use-template-grid">
            {filtered.map((t, idx) => (
              <button key={t.id} type="button" onClick={() => setPicked(t)} data-testid={`use-template-card-${idx}`}
                className={`text-left bg-white rounded-xl border overflow-hidden transition-all ${picked?.id === t.id ? "border-indigo-500 ring-2 ring-indigo-200" : "border-slate-200 hover:shadow-md"}`}>
                <div className="relative aspect-[4/5] bg-white flex items-center justify-center overflow-hidden border-b border-slate-100">
                  <TplThumb t={t} />
                  {picked?.id === t.id && <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow"><Check size={14} /></span>}
                </div>
                <div className="p-2.5">
                  <div className="font-semibold text-sm text-slate-800 truncate">{t.name}</div>
                  <span className="inline-block mt-1 text-[11px] font-medium rounded bg-slate-100 text-slate-500 px-1.5 py-0.5">{t.category}</span>
                </div>
              </button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700" disabled={busy || !picked} onClick={generate} data-testid="use-template-generate-btn">
            {busy ? <Loader2 className="animate-spin" size={16} /> : bindMode ? <Check size={16} /> : <Download size={16} />}{bindMode ? "Use this layout" : "Generate PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
